<template>
  <section class="parking-camera-table">
    <div
      v-if="loading"
      class="parking-camera-table__loader"
      role="status"
      aria-label="Загрузка камер парковки"
    >
      <SpinnerLoading color="blue" />
    </div>

    <PacsTable
      v-else
      :column-captions="columnCaptions"
      :column-names="columnNames"
      :data-rows="dataRows"
      :page-count="1"
      :key-field="'camera_number'"
      :sort-info="[]"
      :total-count="cameras.length"
      :current-page="1"
      custom-row-mode="row"
      :view-only-mode="true"
    >
      <template #customTdCells="{dataRow}">
        <div
          class="cam-row parking-camera-table__row"
          @click="openCamera(dataRow)"
        >
          <div class="cam-row__thumb parking-camera-table__thumb">
            <img
              v-if="getCameraPreview(dataRow)"
              :src="getCameraPreview(dataRow)"
              :alt="getCameraTitle(dataRow)"
              loading="lazy"
            >
            <span v-else class="parking-camera-table__no-preview">
              Превью недоступно
            </span>
          </div>

          <div class="cam-row__meta parking-camera-table__meta">
            <div class="cam-row__title parking-camera-table__title">
              {{ getCameraTitle(dataRow) }}
            </div>
            <div class="cam-row__subtitle parking-camera-table__address">
              {{ getCameraAddress(dataRow) }}
            </div>
          </div>

          <div class="cam-row__actions parking-camera-table__actions" @click.stop>
            <button
              class="cam-row__action cam-row__action--expand"
              type="button"
              @click="openMarkupSettings(dataRow)"
            >
              <svg class="cam-row__icon" aria-hidden="true">
                <use xlink:href="#icon-menu-zones" />
              </svg>
              <span class="cam-row__label">Настройка</span>
            </button>

            <button
              class="cam-row__action cam-row__action--expand"
              type="button"
              @click="openReport(dataRow)"
            >
              <svg class="cam-row__icon" aria-hidden="true">
                <use xlink:href="#icon-menu-report" />
              </svg>
              <span class="cam-row__label">Отчет</span>
            </button>
          </div>
        </div>
      </template>
    </PacsTable>
  </section>
</template>

<script>
import PacsTable from "@/components/pacs/PacsTable.vue";
import RequestTokenCameraDialog from "@/components/pacs/analytics/RequestTokenCameraDialog.vue";
import {ROUTE_PARKING_DETECTION_REPORT} from "@/router/names.js";
import {QUERY_KEY_ONE_SCREEN_CAMERA_NUMBER} from "@/router/queryKeys.js";
import {ACTION_EDIT_PARKING_DETECTION_ANALYTICS} from "@/store/analytics/index.js";
import {ANALYTICS} from "@/store/analytics/helpers.js";
import {
  ACTION_LOAD_CAMERA_FOR_EDIT_ANALYTICS,
  FIELDS_CAMERA,
} from "@/store/cameras/index.js";
import {TOKEN_TTL} from "@/utils/consts.js";
import {PARKING_COLORS} from "@/components/pacs/analytics/parkingDetection/parkingComplexConfig.js";
import {
  AnalyticParkingStop,
  AnalyticParkingStopsConfig,
} from "camsng-frontend-shared/components/smartPlayer/mixins.js";

/**
 * Таблица камер выбранной парковки.
 * Данные приходят от родительского комплекса и не загружаются внутри компонента.
 */
export default {
  name: "TableParkingDetectionAnalytics",
  components: {
    PacsTable,
  },
  props: {
    cameras: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    parking: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      columnCaptions: {},
      columnNames: [],
    };
  },
  computed: {
    dataRows() {
      return this.cameras.map((camera, index) => ({
        _key_: camera.camera_number || camera.number || index,
        __camera: camera,
      }));
    },
  },
  methods: {
    getCamera(dataRow) {
      return dataRow.__camera || {};
    },
    getCameraPreview(dataRow) {
      const camera = this.getCamera(dataRow);
      return camera.preview_url || camera.preview || "";
    },
    getCameraTitle(dataRow) {
      const camera = this.getCamera(dataRow);
      return camera.title || camera.name || camera.camera_number || "Камера без названия";
    },
    getCameraAddress(dataRow) {
      return this.getCamera(dataRow).address || "Адрес не указан";
    },
    getCameraNumber(dataRow) {
      const camera = this.getCamera(dataRow);
      return camera.camera_number || camera.number || "";
    },
    getParkingColor() {
      const selectedColor = PARKING_COLORS.find(color => color.value === this.parking.color);
      return selectedColor ? selectedColor.hex : this.parking.color || PARKING_COLORS[0].hex;
    },
    openCamera(dataRow) {
      const cameraNumber = this.getCameraNumber(dataRow);
      if (!cameraNumber) return;

      this.$router.push({
        name: this.$route.name,
        query: {
          ...(this.$route.query || {}),
          [QUERY_KEY_ONE_SCREEN_CAMERA_NUMBER]: cameraNumber,
        },
      });
    },
    openReport(dataRow) {
      const cameraNumber = this.getCameraNumber(dataRow);
      if (!cameraNumber) return;

      this.$router.push({
        name: ROUTE_PARKING_DETECTION_REPORT,
        params: {cameraNumber},
      });
    },
    async openMarkupSettings(dataRow) {
      const camera = this.getCamera(dataRow);
      const cameraNumber = this.getCameraNumber(dataRow);
      if (!cameraNumber) return;

      try {
        const response = await this.$store.dispatch(
          `cameras/${ACTION_LOAD_CAMERA_FOR_EDIT_ANALYTICS}`,
          {
            fields: [
              FIELDS_CAMERA.number,
              FIELDS_CAMERA.address,
              FIELDS_CAMERA.title,
              FIELDS_CAMERA.is_embed,
              FIELDS_CAMERA.is_sounding,
              FIELDS_CAMERA.analytics,
              FIELDS_CAMERA.is_fav,
              FIELDS_CAMERA.is_public,
              FIELDS_CAMERA.server,
              FIELDS_CAMERA.streams_count,
              FIELDS_CAMERA.token_l,
              FIELDS_CAMERA.token_r,
              FIELDS_CAMERA.token_d,
              FIELDS_CAMERA.permission,
            ],
            tokenDVRTTL: TOKEN_TTL.MULTI,
            tokenDownloadStart: 1,
            numbers: [cameraNumber],
          }
        );
        const cameraInfo = response.data.results[0];
        if (!cameraInfo || !cameraInfo.server) {
          throw new Error("Не удалось загрузить данные камеры");
        }

        const parkingSpots = Array.isArray(camera.parking_spots)
          ? camera.parking_spots
          : [];
        const analyticParkingStopsConfig = new AnalyticParkingStopsConfig(
          camera.image_width,
          camera.image_height,
          4,
          4,
          parkingSpots.map(spot => new AnalyticParkingStop(spot.coordinates, spot.id))
        );

        this.$camsdals.open(
          RequestTokenCameraDialog,
          {
            archiveToken: cameraInfo.token_r,
            downloadToken: cameraInfo.token_d,
            liveToken: cameraInfo.token_l,
            cameraNumber,
            title: cameraInfo.title,
            domain: cameraInfo.server.domain,
            streamCount: cameraInfo.streams_count,
            hasSound: cameraInfo.is_sounding,
            vendorName: cameraInfo.server.vendor_name,
            analyticParkingStopsConfig,
            analyticType: ANALYTICS.parking_detection,
            showSchedule: true,
            parkingName: this.parking.name,
            parkingColor: this.getParkingColor(),
          },
          {dialogTitle: `Просмотр видео с камеры №${cameraNumber} - ${cameraInfo.title}`},
          {
            name: "js-click-outside",
            size: "parking-zones-vuedal",
            onClose: async (configForSave) => {
              if (!configForSave) return;

              try {
                await this.$store.dispatch(
                  `analytics/${ACTION_EDIT_PARKING_DETECTION_ANALYTICS}`,
                  {
                    cameraNumber,
                    imageWidth: configForSave.zonesWidthParking,
                    imageHeight: configForSave.zonesHeightParking,
                    parkingSpots: configForSave.exportParkingStops().map(spot => ({
                      id: spot.id,
                      coordinates: spot.exportCoordsPairs(),
                    })),
                  }
                );
                this.$emit("updated", cameraNumber);
                this.notify("Настройки парковочных мест сохранены", "success");
              } catch (error) {
                this.notify(error.message || "Не удалось сохранить настройки", "error");
              }
            },
          }
        );
      } catch (error) {
        this.notify(error.message || "Не удалось открыть настройки камеры", "error");
      }
    },
    notify(text, type) {
      this.$notify({group: "main", text, duration: 4000, type});
    },
  },
};
</script>
